import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createBlankProject, duplicateProject, normalizeProject, touchProject } from '@/domain/project';
import { defaultStorage, loadProjects, loadUiState, saveProjects, saveUiState } from '@/domain/storage';
import type { StorageLike } from '@/domain/storage';
import type { Project } from '@/domain/types';

export interface ProjectStoreValue {
  /** 首次从 localStorage 读取完成 */
  ready: boolean;
  /** localStorage 是否可用（隐私模式下为 false，此时数据只在当前标签页内有效） */
  storageAvailable: boolean;
  projects: Project[];
  getProject: (id: string) => Project | undefined;
  createProject: (name: string) => Project;
  /** 直接插入一个项目（导入 / 示例） */
  addProject: (project: Project) => Project;
  updateProject: (id: string, updater: (project: Project) => Project) => void;
  removeProject: (id: string) => void;
  duplicate: (id: string) => Project | undefined;
  lastProjectId: string | null;
  setLastProjectId: (id: string | null) => void;
}

const ProjectStoreContext = createContext<ProjectStoreValue | null>(null);

export function ProjectStoreProvider({
  children,
  storage,
}: {
  children: ReactNode;
  /** 单测可以注入内存实现 */
  storage?: StorageLike | null;
}) {
  const resolvedStorage = useRef<StorageLike | null>(storage ?? defaultStorage()).current;
  const storageAvailable = resolvedStorage !== null;

  const [projects, setProjects] = useState<Project[]>([]);
  const [lastProjectId, setLastProjectIdState] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!resolvedStorage) {
      setReady(true);
      return;
    }
    setProjects(loadProjects(resolvedStorage));
    setLastProjectIdState(loadUiState(resolvedStorage).lastProjectId);
    setReady(true);
  }, [resolvedStorage]);

  // 读取完成后才开始回写，避免用初始空数组覆盖已有数据
  useEffect(() => {
    if (!ready || !resolvedStorage) return;
    saveProjects(projects, resolvedStorage);
  }, [projects, ready, resolvedStorage]);

  useEffect(() => {
    if (!ready || !resolvedStorage) return;
    saveUiState({ lastProjectId }, resolvedStorage);
  }, [lastProjectId, ready, resolvedStorage]);

  const getProject = useCallback(
    (id: string) => projects.find((project) => project.id === id),
    [projects],
  );

  const addProject = useCallback((project: Project) => {
    const normalized = normalizeProject(project) ?? project;
    setProjects((current) => [normalized, ...current.filter((item) => item.id !== normalized.id)]);
    return normalized;
  }, []);

  const createProject = useCallback(
    (name: string) => addProject(createBlankProject(name)),
    [addProject],
  );

  const updateProject = useCallback((id: string, updater: (project: Project) => Project) => {
    setProjects((current) =>
      current.map((project) => (project.id === id ? touchProject(updater(project)) : project)),
    );
  }, []);

  const removeProject = useCallback((id: string) => {
    setProjects((current) => current.filter((project) => project.id !== id));
    setLastProjectIdState((current) => (current === id ? null : current));
  }, []);

  const duplicate = useCallback(
    (id: string) => {
      const source = projects.find((project) => project.id === id);
      if (!source) return undefined;
      const copy = duplicateProject(source, `${source.name} 副本`);
      setProjects((current) => [copy, ...current]);
      return copy;
    },
    [projects],
  );

  const setLastProjectId = useCallback((id: string | null) => setLastProjectIdState(id), []);

  const value = useMemo<ProjectStoreValue>(
    () => ({
      ready,
      storageAvailable,
      projects,
      getProject,
      createProject,
      addProject,
      updateProject,
      removeProject,
      duplicate,
      lastProjectId,
      setLastProjectId,
    }),
    [
      ready,
      storageAvailable,
      projects,
      getProject,
      createProject,
      addProject,
      updateProject,
      removeProject,
      duplicate,
      lastProjectId,
      setLastProjectId,
    ],
  );

  return <ProjectStoreContext.Provider value={value}>{children}</ProjectStoreContext.Provider>;
}

export function useProjectStore(): ProjectStoreValue {
  const context = useContext(ProjectStoreContext);
  if (!context) throw new Error('useProjectStore 必须在 ProjectStoreProvider 内使用');
  return context;
}
